// src/pages/Auth/Login.jsx

import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { Eye, EyeOff, User as UserIcon, Lock, Mail, Phone } from "lucide-react";

import "../../../assets/css/home/DonorsLogin.css";
import AppContext from "../../../context/AppContext";
import Swal from "sweetalert2";

const ReceiverLogin = () => {
  const navigate = useNavigate();
  const { baseUrl, regExpchecker, storeLoggedInReceiverId } = useContext(AppContext);

  const [isLoginMode, setIsLoginMode]   = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fullname, setFullname] = useState("");
  const [email, setEmail]       = useState("");
  const [phone, setPhone]       = useState("");
  const [password, setPassword] = useState("");

  const [errors, setErrors] = useState({});

  // ✅ Validate before submit
  const validateBeforeSubmit = () => {
    const newErrors = {};

    if (!isLoginMode) {
      // ── Signup validation ──
      if (!fullname || fullname.trim() === "") {
        newErrors.name = "Full name is required";
      }
      if (!phone || phone.trim() === "") {
        newErrors.phone = "Phone number is required";
      }
      const passErr = regExpchecker("password", password);
      if (passErr) newErrors.password = passErr;

      // Email regex signup mein
      const emailErr = regExpchecker("email", email);
      if (emailErr) newErrors.email = emailErr;

    } else {
      // ── Login mein sirf empty check — koi regex nahi ──
      if (!email || email.trim() === "") {
        newErrors.email = "Email is required";
      }
      if (!password || password.trim() === "") {
        newErrors.password = "Password is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const toggleAuthMode = () => {
    setIsLoginMode(!isLoginMode);
    setErrors({});
    setEmail("");
    setPassword("");
    setFullname("");
    setPhone("");
  };

  const handleSocialLogin = (provider) => {
    alert(`${provider} ${isLoginMode ? "Login" : "Signup"} will be integrated!`);
  };

  // ✅ Signup
  const receiverSignup = () => {
    if (!validateBeforeSubmit()) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("fullname", fullname);
    formData.append("email",    email);
    formData.append("phone",    phone);
    formData.append("password", password);

    axios.post(`${baseUrl}/receiver/auth/signup`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            title: "Congratulations!",
            text: "Account created successfully. Please Login",
            icon: "success",
          });
          toggleAuthMode();
        } else {
          const errMsg = response.data.message || response.data.error || "Cannot create account. Try again later.";
          Swal.fire({ title: "Oops!", text: errMsg, icon: "error" });
        }
      })
      .catch((error) => {
        const errMsg = error.response?.data?.message || "Something went wrong. Please try again.";
        Swal.fire({ title: "Error!", text: errMsg, icon: "error" });
      })
      .finally(() => setIsSubmitting(false));
  };

  // ✅ Login
  const receiverLogin = () => {
    if (!validateBeforeSubmit()) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("email",    email);
    formData.append("password", password);

    axios.post(`${baseUrl}/receiver/auth/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.token) {
          localStorage.setItem("loggedInReceiver", JSON.stringify(response.data.receiver));
          localStorage.setItem("receiveremail",    email);
          localStorage.setItem("receiverpassword", password);
          storeLoggedInReceiverId(response.data.receiver.id);
          navigate("/receiver/dashboard");
        } else {
          const errMsg = response.data.Message || response.data.message || response.data.error || "Invalid email or password.";
          Swal.fire({ title: "Login Failed!", text: errMsg, icon: "error" });
        }
      })
      .catch((error) => {
        const errMsg = error.response?.data?.message || "Something went wrong. Please try again.";
        Swal.fire({ title: "Error!", text: errMsg, icon: "error" });
      })
      .finally(() => setIsSubmitting(false));
  };

  // ✅ Auto Login
  const receiverAutoLogin = (em, pw) => {
    const formData = new FormData();
    formData.append("email",    em);
    formData.append("password", pw);

    axios.post(`${baseUrl}/receiver/auth/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.token) {
          localStorage.setItem("loggedInReceiver", JSON.stringify(response.data.receiver));
          storeLoggedInReceiverId(response.data.receiver.id);
          navigate("/receiver/dashboard");
        }
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    const storedEmail    = localStorage.getItem("receiveremail");
    const storedPassword = localStorage.getItem("receiverpassword");
    if (storedEmail && storedPassword) {
      receiverAutoLogin(storedEmail, storedPassword);
    }
  }, []);

  return (
    <div className="login-page">
      <div className="login-wrapper">
        <div className="login-container">

          {/* LEFT SIDE */}
          <div className="login-form-section">
            <h2 className="login-title">{isLoginMode ? "Login" : "Sign Up"}</h2>

            <Form onSubmit={(e) => {
              e.preventDefault();
              if (isLoginMode) { receiverLogin(); } else { receiverSignup(); }
            }}>

              {/* FULL NAME - only signup */}
              {!isLoginMode && (
                <Form.Group className="form-group">
                  <div className="input-wrapper">
                    <UserIcon className="input-icon" size={20} />
                    <Form.Control
                      type="text"
                      placeholder="Full Name"
                      value={fullname}
                      onChange={(e) => setFullname(e.target.value)}
                      className={`form-input ${errors.name ? "is-invalid" : ""}`}
                    />
                  </div>
                  {errors.name && <small className="text-danger ms-1">{errors.name}</small>}
                </Form.Group>
              )}

              {/* EMAIL */}
              <Form.Group className="form-group">
                <div className="input-wrapper">
                  <Mail className="input-icon" size={20} />
                  <Form.Control
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`form-input ${errors.email ? "is-invalid" : ""}`}
                  />
                </div>
                {errors.email && <small className="text-danger ms-1">{errors.email}</small>}
              </Form.Group>

              {/* PHONE - only signup */}
              {!isLoginMode && (
                <Form.Group className="form-group">
                  <div className="input-wrapper">
                    <Phone className="input-icon" size={20} />
                    <Form.Control
                      type="tel"
                      placeholder="Phone Number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={`form-input ${errors.phone ? "is-invalid" : ""}`}
                    />
                  </div>
                  {errors.phone && <small className="text-danger ms-1">{errors.phone}</small>}
                </Form.Group>
              )}

              {/* PASSWORD */}
              <Form.Group className="form-group">
                <div className="input-wrapper">
                  <Lock className="input-icon" size={20} />
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`form-input ${errors.password ? "is-invalid" : ""}`}
                  />
                  <div className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </div>
                </div>
                {errors.password && <small className="text-danger ms-1">{errors.password}</small>}
              </Form.Group>

              {/* BUTTON */}
              <Button type="submit" className="login-btn" disabled={isSubmitting}>
                {isSubmitting ? "Processing..." : isLoginMode ? "Login" : "Sign Up"}
              </Button>

              {/* SOCIAL LOGIN */}
              <div className="social-login-section">
                <div className="divider"><span>OR</span></div>
                <div className="social-buttons">
                  <button type="button" className="social-btn google-btn" onClick={() => handleSocialLogin("Google")}>
                    Login with Google
                  </button>
                  <button type="button" className="social-btn facebook-btn" onClick={() => handleSocialLogin("Facebook")}>
                    Login with Facebook
                  </button>
                </div>
              </div>

              {/* FOOTER */}
              <div className="form-footer">
                <span className="footer-link">
                  {isLoginMode ? "Don't have account?" : "Already have an account?"}
                </span>
                <span className="signup-link" onClick={toggleAuthMode}>
                  {isLoginMode ? "Sign up" : "Login"}
                </span>
              </div>

            </Form>
          </div>

          {/* RIGHT SIDE */}
          <div className="first-welcome-section">
            <div className="welcome-content">
              <h1 className="welcome-title">
                {isLoginMode ? (<>WELCOME<br />BACK!</>) : (<>JOIN<br />US!</>)}
              </h1>
              <p className="first-welcome-text">
                {isLoginMode
                  ? "We're happy to have you back again!"
                  : "Start your journey with us today!"}
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ReceiverLogin;